// Language preference: a supported catalog locale ('en'|'es'|…|'zh-Hans') or 'auto'.
// Mirrors theme.js exactly — persisted per-device in localStorage; the account copy
// (agent.prefs.locale / endUser.language) is applied on load only when this device
// hasn't chosen. Detection walks navigator.languages via the SHARED matchLocale so
// client + server agree. `formatLocale` (the full browser region tag, e.g. es-MX) is
// kept separately for Intl date/number formatting; the catalog is region-collapsed.
import { record } from './activity.js';
import { EVENT, SUPPORTED_LOCALES, matchLocale } from '@essential-support/shared/constants';
import i18n, { loadCatalog } from '../i18n.js';

const STORAGE_KEY = 'es_locale';
let formatLocale = 'en';

function browserTags() {
  const langs = (typeof navigator !== 'undefined'
    && (navigator.languages || (navigator.language ? [navigator.language] : []))) || [];
  return langs.length ? langs : ['en'];
}
// Best full region tag (for Intl) that resolves to `catalog`, else the catalog itself.
function formatLocaleFor(catalog) {
  for (const tag of browserTags()) if (matchLocale(tag) === catalog) return tag;
  return catalog;
}
// Detect from the browser: first supported tag wins, else English.
function detect() {
  for (const tag of browserTags()) { const m = matchLocale(tag); if (m) return { catalog: m, format: tag }; }
  return { catalog: 'en', format: browserTags()[0] };
}
// Active locale = explicit stored preference (if valid), else browser detection.
function resolveActive() {
  const stored = getStoredLocale();
  if (stored && stored !== 'auto' && SUPPORTED_LOCALES.includes(stored)) {
    return { catalog: stored, format: formatLocaleFor(stored) };
  }
  return detect();
}

export function getStoredLocale() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
export function setStoredLocale(value) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}
export function getFormatLocale() { return formatLocale; }
export function currentLocale() { return i18n.language || 'en'; }

// Apply a catalog locale (+ its Intl format locale): load the catalog if needed,
// switch i18next, set <html lang>. Non-English catalogs load async, so the UI shows
// English then re-renders when the catalog lands — same graceful-degradation as the
// theme flash-avoidance. Safe to call before first paint. Returns a promise that settles
// once the language is switched (main.jsx waits on it for a cached first render — gotcha 25).
export function applyLocale(catalog, format) {
  const cat = SUPPORTED_LOCALES.includes(catalog) ? catalog : 'en';
  formatLocale = format || cat;
  try { document.documentElement.lang = formatLocale; } catch { /* ignore */ }
  const go = () => i18n.changeLanguage(cat);
  return (cat === 'en' ? Promise.resolve(go()) : loadCatalog(cat).then(go, go)).catch(() => {});
}
// Apply an account-level locale (no device override present) — resolves its Intl tag.
export function applyAccountLocale(catalog) { return applyLocale(catalog, formatLocaleFor(catalog)); }

// Apply the active (stored-or-detected) locale as early as possible.
export function initLocale() {
  const { catalog, format } = resolveActive();
  return applyLocale(catalog, format);
}

// User picked a language from the switcher. `'auto'` clears the pref (→ detection).
export function setLocalePreference(value) {
  setStoredLocale(value);
  record(EVENT.UI_LOCALE_CHANGED, { locale: value });
  const { catalog, format } = resolveActive();
  applyLocale(catalog, format);
}
